import React from 'react';
import { ParameterService } from 'services/ParametersProviderApi/ParametersService';
import { SearchOptions, MealPlanNames, Filters, StarRating, ISearchQuery, Occasion } from 'services/BackendApi';
import { IFiltersCategory } from 'services/BackendApi/types/SearchOptionsResponse';
import RangeInput, { RangeValueType } from 'pureUi/RangeInput';
import styled from 'styled-components';
import { pureUiTheme } from 'pureUi/pureUiTheme';
import Info from 'pureUi/Info';
import { FilterChipGroup } from 'ui/FilterChip/FilterChipGroup';
import { IFilterChipOption } from 'ui/FilterChip/types';

const sectionLabelCls =
  'block font-hurmegeometric-sans font-bold text-[12px] leading-none tracking-[0px] uppercase text-black mb-[10px]';

export interface SearchSettingsProps extends React.HTMLProps<HTMLDivElement> {
  options: SearchOptions;
  query: ISearchQuery;
  canSubmit: boolean;
  showRegions: boolean;
  onFilterChange: (filter: Filters) => void;
  onStarRatingChange: (rating: StarRating, checked: boolean) => void;
  onOccasionChange: (occasion: Occasion, checked: boolean) => void;
  onMealPlanChange: (mealPlan: MealPlanNames, checked: boolean) => void;
  onToggleMealPlan: (mealPlan: MealPlanNames) => void;
  onRegionChange: (region: string, checked: boolean) => void;
  onToggleShowRegions: () => void;
  onPriceRangeChange: (type: RangeValueType, value: string) => void;
  onSubmit: () => void;
}

export const SearchSettings = (props: SearchSettingsProps) => {
  const { ENABLE_REGION_FILTER } = ParameterService.getParameters();

  const handleOccasionsChange = (newValues: string[]) => {
    const currentSelected = Object.values(Occasion).filter(o => props.query.lodgings[0][o]);
    newValues
      .filter(v => !currentSelected.includes(v as Occasion))
      .forEach(v => props.onOccasionChange(v as Occasion, true));
    currentSelected
      .filter(v => !newValues.includes(v))
      .forEach(v => props.onOccasionChange(v, false));
  };

  const handleRegionsChange = (newValues: string[]) => {
    const currentSelected = props.query.regions;
    if (newValues.length > 0 && !props.showRegions) {
      props.onToggleShowRegions();
    } else if (newValues.length === 0 && props.showRegions) {
      props.onToggleShowRegions();
    }
    newValues
      .filter(v => !currentSelected.includes(v))
      .forEach(v => props.onRegionChange(v, true));
    currentSelected
      .filter(v => !newValues.includes(v))
      .forEach(v => props.onRegionChange(v, false));
  };

  const handleMealPlanChange = (newValues: string[]) => {
    if (newValues.length === 0) {
      props.onMealPlanChange(MealPlanNames.ANY, true);
    } else {
      const currentSpecific = props.query.mealPlanCategories.filter(m => m !== MealPlanNames.ANY) as MealPlanNames[];
      newValues
        .filter(v => !currentSpecific.includes(v as MealPlanNames))
        .forEach(v => props.onToggleMealPlan(v as MealPlanNames));
      currentSpecific
        .filter(v => !newValues.includes(v))
        .forEach(v => props.onToggleMealPlan(v as MealPlanNames));
    }
  };

  const handleStarRatingsChange = (newValues: string[]) => {
    const currentSelected = props.query.starRatings;
    newValues
      .filter(v => !currentSelected.includes(v as StarRating))
      .forEach(v => props.onStarRatingChange(v as StarRating, true));
    currentSelected
      .filter(v => !newValues.includes(v))
      .forEach(v => props.onStarRatingChange(v, false));
  };

  const handleFiltersCategoryChange = (category: IFiltersCategory) => (newValues: string[]) => {
    const categoryFilterNames = category.filters.map(f => f.filterEnumName as string);
    const currentSelected = props.query.filters.filter(f => categoryFilterNames.includes(f));
    newValues
      .filter(v => !currentSelected.includes(v as Filters))
      .forEach(v => props.onFilterChange(v as Filters));
    currentSelected
      .filter(v => !newValues.includes(v))
      .forEach(v => props.onFilterChange(v));
  };

  const occasionOptions: IFilterChipOption[] = Object.values(Occasion)
    .map(o => ({
      value: o,
      label: o.charAt(0).toUpperCase() + o.slice(1),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const selectedOccasions = Object.values(Occasion).filter(o => Boolean(props.query.lodgings[0][o]));

  const regionOptions: IFilterChipOption[] = props.options.regions
    .filter(r => r?.trim().length > 0)
    .map(r => ({ value: r, label: r }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const mealPlanOptions: IFilterChipOption[] = Object.values(MealPlanNames)
    .filter(m => m !== MealPlanNames.ANY)
    .map(m => ({ value: m, label: m }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const starRatingOptions: IFilterChipOption[] = props.options.starRatings
    .map(sr => ({
      value: sr,
      label: `${sr} Star`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <div className={props.className}>
      <div className="sidebar-group mb-1rem p-18px bg-gray-light">
        <span className={sectionLabelCls}>Occasions</span>
        <div className="normal-case">
          <FilterChipGroup
            options={occasionOptions}
            selectedValues={selectedOccasions}
            onChange={handleOccasionsChange}
          />
        </div>
      </div>

      {ENABLE_REGION_FILTER && (
        <div className="sidebar-group mb-1rem p-18px bg-gray-light">
          <span className={sectionLabelCls}>Regions</span>
          <div className="normal-case">
            <FilterChipGroup
              options={regionOptions}
              selectedValues={props.query.regions}
              onChange={handleRegionsChange}
            />
          </div>
        </div>
      )}

      <div className="sidebar-group mb-1rem p-18px bg-gray-light">
        <span className={sectionLabelCls}>Price Range</span>
        <RangeInput
          min={props.query.priceRange.min?.toString() || ''}
          max={props.query.priceRange.max?.toString() || ''}
          onChange={props.onPriceRangeChange}
        />
      </div>

      <div className="sidebar-group mb-1rem p-18px bg-gray-light">
        <div className="flex items-center gap-[5px] mb-[10px]">
          <span className="font-hurmegeometric-sans font-bold text-[12px] leading-none tracking-[0px] uppercase text-black">
            Meal Plan
          </span>
          <Info className="mealplanInfo">
            <ul className="mealplanTooltip">
              <li>
                <span>RO</span> Room Only
              </li>
              <li>
                <span>BB</span> Breakfast Included
              </li>
              <li>
                <span>HB</span> Breakfast &amp; Dinner (Drinks excluded)
              </li>
              <li>
                <span>FB</span> 3 Meals a day (Drinks Excluded)
              </li>
              <li>
                <span>AI</span> All Inclusive
              </li>
            </ul>
          </Info>
        </div>
        <div className="normal-case">
          <FilterChipGroup
            options={mealPlanOptions}
            selectedValues={props.query.mealPlanCategories.filter(m => m !== MealPlanNames.ANY)}
            onChange={handleMealPlanChange}
          />
        </div>
      </div>

      <div className="sidebar-group mb-1rem p-18px bg-gray-light">
        <span className={sectionLabelCls}>Star Rating</span>
        <div className="normal-case">
          <FilterChipGroup
            options={starRatingOptions}
            selectedValues={props.query.starRatings}
            onChange={handleStarRatingsChange}
          />
        </div>
      </div>

      {props.options.filtersCategories.map(category => (
        <div key={category.categoryTitle} className="sidebar-group mb-1rem p-18px bg-gray-light">
          <span className={sectionLabelCls}>{category.categoryTitle}</span>
          <div className="normal-case">
            <FilterChipGroup
              options={category.filters
                .map(f => ({ value: f.filterEnumName, label: f.filterTitle }))
                .sort((a, b) => a.label.localeCompare(b.label))}
              selectedValues={props.query.filters.filter(f =>
                category.filters.some(cf => cf.filterEnumName === f)
              )}
              onChange={handleFiltersCategoryChange(category)}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default styled(SearchSettings)`
  .mealplanInfo {
    position: relative;
    top: 0;
    margin-left: 5px;
    font-size: 15px;
    color: ${pureUiTheme.colors.gold};
  }

  .mealplanTooltip {
    margin: 20px;
    list-style: none;
    padding: 0;

    li {
      display: flex;
      flex-direction: row;
      margin-bottom: 8px;
    }

    li > span {
      font-weight: bold;
      color: ${pureUiTheme.colors.gold};
      min-width: 30px;
    }
  }


`;
